import { cleanString } from '../apiUtils';

const STRING_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'exists', label: 'exists' },
];

const NUMBER_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'gt', label: 'is greater than' },
  { value: 'gte', label: 'is greater than or equal to' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is less than or equal to' },
  { value: 'between', label: 'between (min,max)' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'exists', label: 'exists' },
];

const BOOLEAN_OPERATORS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'exists', label: 'exists' },
];

const MEASUREMENT_OPERATORS = [
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'exists', label: 'exists' },
];

const ENUM_OPERATORS = [
  { value: 'is', label: 'is' },
  { value: 'is_not', label: 'is not' },
  { value: 'is_any_of', label: 'is any of' },
  { value: 'is_none_of', label: 'is none of' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const TEMPORAL_OPERATORS = [
  { value: 'within_past', label: 'within past' },
  { value: 'within_next', label: 'within next' },
  { value: 'older_than', label: 'older than' },
  { value: 'is_overdue', label: 'is overdue' },
  { value: 'is_today', label: 'is today' },
  { value: 'is_this', label: 'is this' },
];

const VALUELESS_OPERATORS = new Set(['is_empty', 'is_not_empty']);
const TEMPORAL_VALUE_OPERATORS = new Set(['within_past', 'within_next', 'older_than']);
const TEMPORAL_VALUELESS_OPERATORS = new Set(['is_overdue', 'is_today']);
const TEMPORAL_CALENDAR_OPERATORS = new Set(['is_this']);
const ENUM_MULTI_OPERATORS = new Set(['is_any_of', 'is_none_of']);

export const COMPOSER_RELATIVE_DATE_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

export const COMPOSER_RELATIVE_CALENDAR_UNITS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const TYPE_GROUPS = {
  string: 'string',
  text: 'string',
  textarea: 'string',
  richtext: 'string',
  url: 'string',
  email: 'string',
  phone: 'string',

  number: 'number',
  currency: 'number',
  percentage: 'number',
  int: 'number',
  integer: 'number',
  float: 'number',
  decimal: 'number',

  boolean: 'boolean',
  bool: 'boolean',
  checkbox: 'boolean',

  measurement: 'measurement',

  enum: 'enum',
  multiselect: 'enum',
  tags: 'enum',
  people: 'enum',

  date: 'temporal',
  datetime: 'temporal',
  daterange: 'temporal',
  datetimerange: 'temporal',
};

const GROUP_OPERATORS = {
  string: STRING_OPERATORS,
  number: NUMBER_OPERATORS,
  boolean: BOOLEAN_OPERATORS,
  measurement: MEASUREMENT_OPERATORS,
  enum: ENUM_OPERATORS,
  temporal: TEMPORAL_OPERATORS,
};

export function resolveComposerFieldType(fieldDef, fallbackType = 'string') {
  return cleanString(fieldDef?.type || fallbackType || 'string').toLowerCase() || 'string';
}

export function isComposerTemporalFieldType(fieldType) {
  const normalized = cleanString(fieldType).toLowerCase();
  return normalized === 'date'
    || normalized === 'datetime'
    || normalized === 'daterange'
    || normalized === 'datetimerange';
}

export function normalizeComposerRelativeUnit(unit) {
  const normalized = cleanString(unit).toLowerCase();
  if (normalized === 'day' || normalized === 'days') return 'days';
  if (normalized === 'week' || normalized === 'weeks') return 'weeks';
  if (normalized === 'month' || normalized === 'months') return 'months';
  if (normalized === 'year' || normalized === 'years') return 'years';
  return '';
}

export function normalizeComposerCalendarUnit(unit) {
  const normalized = cleanString(unit).toLowerCase();
  if (normalized === 'week' || normalized === 'weeks') return 'week';
  if (normalized === 'month' || normalized === 'months') return 'month';
  if (normalized === 'year' || normalized === 'years') return 'year';
  return '';
}

export function isComposerTemporalOperatorNeedsValue(operator) {
  return TEMPORAL_VALUE_OPERATORS.has(cleanString(operator).toLowerCase());
}

export function isComposerTemporalOperatorNeedsUnit(operator) {
  const normalized = cleanString(operator).toLowerCase();
  return TEMPORAL_VALUE_OPERATORS.has(normalized) || TEMPORAL_CALENDAR_OPERATORS.has(normalized);
}

export function isComposerTemporalValuelessOperator(operator) {
  return TEMPORAL_VALUELESS_OPERATORS.has(cleanString(operator).toLowerCase());
}

export function isComposerEnumMultiOperator(operator) {
  return ENUM_MULTI_OPERATORS.has(cleanString(operator).toLowerCase());
}

function resolveOperatorGroup(fieldType) {
  const normalized = cleanString(fieldType).toLowerCase() || 'string';
  return TYPE_GROUPS[normalized] || 'string';
}

export function getComposerOperatorOptions(fieldType) {
  const group = resolveOperatorGroup(fieldType);
  return GROUP_OPERATORS[group] || STRING_OPERATORS;
}

export function resolveComposerDefaultOperator(fieldType) {
  return getComposerOperatorOptions(fieldType)[0]?.value || 'eq';
}

export function isComposerOperatorAllowed(fieldType, operator) {
  const normalizedOperator = cleanString(operator).toLowerCase();
  return getComposerOperatorOptions(fieldType).some((row) => row.value === normalizedOperator);
}

export function normalizeComposerOperatorForFieldType(fieldType, operator) {
  const normalizedOperator = cleanString(operator).toLowerCase();
  if (isComposerOperatorAllowed(fieldType, normalizedOperator)) {
    return normalizedOperator;
  }
  return resolveComposerDefaultOperator(fieldType);
}

export function isComposerValuelessOperator(operator) {
  return VALUELESS_OPERATORS.has(cleanString(operator).toLowerCase());
}

export function isComposerExistsOperator(operator) {
  return cleanString(operator).toLowerCase() === 'exists';
}
